import { useEffect, useRef, useState } from "react";
import Title from "../../../components/Admin/Title";
import useIsAdmin from "../../../hooks/useIsAdmin";
import ErrorPage from "../../ErrorPage";
import Button from "../../../components/Button";
import {
  faDownload,
  faMagnifyingGlass,
  faMagnifyingGlassPlus,
  faRotateLeft,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

const CreateAIWFromImage = () => {
  const { access } = useIsAdmin();
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [rawCoords, setRawCoords] = useState<{ x: number; y: number }[]>([]);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    document.title = "LVF1 - Create AIW";
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setRawCoords([]); // limpiar coordenadas si se cambia la imagen
      setImageSrc(event.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleImageClick = (
    e: React.MouseEvent<HTMLImageElement, MouseEvent>,
  ) => {
    if (!imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;

    const newCoord = { x: Math.round(x), y: Math.round(y) };
    setRawCoords((prev) => [...prev, newCoord]);
  };

  const getFormattedCoords = () => {
    const total = rawCoords.length;
    return rawCoords.map((coord, i) => {
      const ratio = i / total;
      let sector = 3;
      if (ratio < 1 / 3) sector = 1;
      else if (ratio < 2 / 3) sector = 2;

      return {
        coords: [
          parseFloat(coord.x.toFixed(4)),
          parseFloat(coord.y.toFixed(4)),
        ],
        sector,
        orden: i + 1,
      };
    });
  };

  const handleDownloadJSON = () => {
    const jsonString = JSON.stringify(formattedCoords);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "data.json";
    a.click();

    URL.revokeObjectURL(url);
  };

  const handleRemoveLast = () => {
    setRawCoords((prev) => prev.slice(0, -1));
  };

  if (!access) return <ErrorPage />;

  const formattedCoords = getFormattedCoords();

  return (
    <div className="full-height padding-md app-container flex-center-top page-transition">
      <div className="form-container">
        <Title text="Create AIW From Image" />
        <small className="mandatory-fields">(*) Required fields</small>

        <input
          type="file"
          id="file-input"
          accept="image/*"
          className="form-control"
          onChange={handleFileChange}
        />

        {imageSrc && (
          <>
            <div style={{ marginTop: "1rem", display: "flex", gap: "1rem" }}>
              <Button
                text={"Zoom In"}
                onClick={() => setZoom((z) => Math.min(z + 0.25, 20))}
                icon={faMagnifyingGlassPlus}
                className="button-neutral-lvf1"
                iconPosition="left"
              />
              <Button
                text={"Zoom Out"}
                onClick={() => setZoom((z) => Math.max(z - 0.25, 0.25))}
                icon={faMagnifyingGlassPlus}
                className="button-neutral-lvf1"
                iconPosition="left"
              />
              <Button
                text={"Reset Zoom"}
                onClick={() => setZoom(1)}
                icon={faRotateLeft}
                className="button-neutral-lvf1"
                iconPosition="left"
              />
              <Button
                text={"Delete Last Coord"}
                onClick={handleRemoveLast}
                icon={faTrash}
                className="button-red-lvf1"
                iconPosition="left"
              />
            </div>

            <div
              id="aiw-image-container"
              style={{ position: "relative", marginTop: "1rem" }}
            >
              <img
                src={imageSrc}
                alt="preview"
                ref={imageRef}
                onClick={handleImageClick}
                style={{
                  transform: `scale(${zoom})`,
                  transformOrigin: "top left",
                  cursor: "crosshair",
                  display: "block",
                }}
              />

              {rawCoords.map((coord, idx) => (
                <div
                  key={idx}
                  className="dot"
                  style={{
                    position: "absolute",
                    left: `${coord.x * zoom}px`,
                    top: `${coord.y * zoom}px`,
                    width: `3px`,
                    height: `3px`,
                    backgroundColor: "red",
                    borderRadius: "50%",
                    transform: "translate(-50%, -50%)",
                    pointerEvents: "none",
                  }}
                ></div>
              ))}
            </div>

            <textarea
              value={JSON.stringify(formattedCoords, null, 2)}
              readOnly
              style={{
                width: "100%",
                height: "200px",
                marginTop: "0.5rem",
                fontFamily: "monospace",
                fontSize: "0.85rem",
              }}
            />
            {formattedCoords.length > 0 && (
              <Button
                text={"Download AIW"}
                onClick={handleDownloadJSON}
                icon={faDownload}
                className="button-neutral-lvf1"
                iconPosition="left"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CreateAIWFromImage;
